import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react'
import { isAxiosError } from 'axios'
import { authAPI } from '../config/api'

interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  role?: string
  createdAt?: string
  lastLoginAt?: string | null
  hasAcceptedPrivacyPolicy?: boolean
  marketingEmailsOptIn?: boolean
  consentUpdatedAt?: string | null
  creditBalance?: number
  subscriptionTier?: string | null
  billing?: {
    subscriptionStatus?: string | null
    renewalAt?: string | null
    stripeCustomerId?: string | null
  } | null
}

interface AuthContextType {
  user: User | null
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isLoading: boolean
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await authAPI.profile()
      const userData = data.data as User
      setUser(userData)
    } catch (error) {
      setUser(null)
      throw error
    }
  }, [])

  useEffect(() => {
    const initializeSession = async () => {
      try {
        await refreshProfile()
      } catch (error) {
        // ignore errors; user stays logged out
      } finally {
        setIsLoading(false)
      }
    }

    initializeSession()
  }, [refreshProfile])

  const login = async (email: string, password:string) => {
    try {
      const response = await authAPI.login(email, password);
      const { user: userData } = response.data.data;
      setUser(userData);
    } catch (error) {
      const message = isAxiosError(error)
        ? error.response?.data?.error?.message ?? 'Login failed'
        : 'Login failed';
      throw new Error(message);
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout failed', error);
      // Optionally re-throw or handle the error to notify the user
      throw new Error('Logout failed. Please try again.');
    }
  };

  const value = {
    user,
    login,
    logout,
    isLoading,
    refreshProfile,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
